// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// is stored server-side. The AI brain (narrate / ask) is called separately with only
// a small DERIVED-SIGNALS slice (see signals.rs / brain.ts), never the raw rows.
//
// Data model (multi-vehicle):
//   vehicle { name, make, model, year, plate, distance_unit, volume_unit }
//   fillup  { vehicle_id, ts, odometer, volume, cost, is_full_tank, note }
//   service { vehicle_id, ts, odometer, kind, cost, note }

import { invoke } from "./tauri-web";

export type DistanceUnit = "mi" | "km";
export type VolumeUnit = "gal" | "L";

export interface Vehicle {
  id: string;
  name: string;
  make: string;
  model: string;
  /** 0 = unspecified. */
  year: number;
  plate: string;
  distance_unit: DistanceUnit;
  volume_unit: VolumeUnit;
  /** User-set service-reminder cadence (0 = off). The user's OWN interval — the app states the
   *  elapsed FACT since the last service, never "due"/"overdue" (observational ceiling). */
  service_interval_km: number;
  service_interval_days: number;
  created_at: string;
  updated_at: string;
}
export interface NewVehicle {
  name: string;
  make?: string;
  model?: string;
  year?: number;
  plate?: string;
  distance_unit?: DistanceUnit;
  volume_unit?: VolumeUnit;
  service_interval_km?: number;
  service_interval_days?: number;
}
export type VehiclePatch = Partial<{
  name: string;
  make: string;
  model: string;
  year: number;
  plate: string;
  distance_unit: DistanceUnit;
  volume_unit: VolumeUnit;
  service_interval_km: number;
  service_interval_days: number;
}>;

/** Factual "elapsed since last service" against the user's OWN interval. States the fact only —
 *  no "due"/"overdue"/advice (the app's observational ceiling). null when the reminder is off. */
export interface ServiceStatus {
  km_since: number | null;
  days_since: number | null;
  interval_km_reached: boolean;
  interval_days_reached: boolean;
  /** The date the day/time interval elapses (for a local reminder). null when off / no service yet. */
  next_date: string | null;
}
export function serviceStatus(v: Vehicle, lastService: Service | null, currentOdometer: number | null): ServiceStatus | null {
  if ((v.service_interval_km ?? 0) <= 0 && (v.service_interval_days ?? 0) <= 0) return null;
  const kmSince = currentOdometer != null && lastService ? Math.max(0, currentOdometer - lastService.odometer) : null;
  const daysSince = lastService ? Math.floor((Date.now() - new Date(lastService.ts).getTime()) / 86_400_000) : null;
  let nextDate: string | null = null;
  if (v.service_interval_days > 0 && lastService) {
    const d = new Date(lastService.ts);
    d.setDate(d.getDate() + v.service_interval_days);
    nextDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  return {
    km_since: kmSince,
    days_since: daysSince,
    interval_km_reached: v.service_interval_km > 0 && kmSince != null && kmSince >= v.service_interval_km,
    interval_days_reached: v.service_interval_days > 0 && daysSince != null && daysSince >= v.service_interval_days,
    next_date: nextDate,
  };
}

export interface Fillup {
  id: string;
  vehicle_id: string;
  /** RFC3339 timestamp. */
  ts: string;
  odometer: number;
  volume: number;
  /** Total paid, in the USER's own currency (their record, never a price Oriora charges). */
  cost: number;
  is_full_tank: boolean;
  note: string;
  created_at: string;
  updated_at: string;
  /** On-device receipt/proof photo (compressed-JPEG data-URL), or "". Never sent to AI. */
  photo: string;
}
export interface NewFillup {
  vehicle_id: string;
  ts?: string;
  odometer: number;
  volume: number;
  cost: number;
  is_full_tank?: boolean;
  note?: string;
  /** Optional on-device receipt/proof photo (data-URL). */
  photo?: string;
}
export type FillupPatch = Partial<{
  ts: string;
  odometer: number;
  volume: number;
  cost: number;
  is_full_tank: boolean;
  note: string;
  photo: string;
}>;

export interface Service {
  id: string;
  vehicle_id: string;
  ts: string;
  odometer: number;
  /** Category, normalized lowercase (e.g. "oil-change"). */
  kind: string;
  cost: number;
  note: string;
  created_at: string;
  updated_at: string;
}
export interface NewService {
  vehicle_id: string;
  ts?: string;
  odometer: number;
  kind: string;
  cost: number;
  note?: string;
}
export type ServicePatch = Partial<{
  ts: string;
  odometer: number;
  kind: string;
  cost: number;
  note: string;
}>;

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::GarageSignals. Every field is a computed aggregate —
// economy averages, deltas, cost-per-distance, service intervals, spend split, projection —
// never a raw note. This is the payload the AI narration/answer reasons over (see brain.ts).
export interface VehicleMeta {
  name: string;
  label: string;
  distance_unit: DistanceUnit;
  volume_unit: VolumeUnit;
  economy_unit: string;
  cost_per_distance_unit: string;
}
export interface Overall {
  fillups: number;
  services: number;
  first_odometer: number | null;
  latest_odometer: number | null;
  distance_tracked: number | null;
  first_date: string | null;
  latest_date: string | null;
  days_tracked: number;
}
export interface EconomyPoint {
  date: string;
  distance: number;
  volume: number;
  economy: number;
  cost: number;
}
export interface Economy {
  points: EconomyPoint[];
  lifetime_avg: number | null;
  recent_avg: number | null;
  prior_avg: number | null;
  delta: number | null;
  direction: "improving" | "declining" | "flat" | null;
  slope_per_fill: number | null;
  best: number | null;
  worst: number | null;
  recent_vs_lifetime_pct: number | null;
  off_best_pct: number | null;
}
export interface CostPerDistance {
  recent: number | null;
  prior: number | null;
  delta: number | null;
  pct_change: number | null;
  direction: "up" | "down" | "flat" | null;
  lifetime: number | null;
}
export interface ServiceInterval {
  kind: string;
  count: number;
  avg_distance_between: number | null;
  avg_days_between: number | null;
  last_date: string;
  last_odometer: number;
  distance_since_last: number | null;
  days_since_last: number;
  ratio_vs_own_avg_distance: number | null;
  ratio_vs_own_avg_days: number | null;
  /** Longer than the owner's OWN usual gap (FACT, never "overdue"). */
  longer_than_usual: boolean;
}
export interface LastService {
  kind: string;
  date: string;
  odometer: number;
  days_since: number;
}
export interface ServiceStats {
  total_events: number;
  total_spend: number;
  intervals: ServiceInterval[];
  last_event: LastService | null;
}
export interface CategorySpend {
  kind: string;
  recent: number;
  prior: number;
  delta: number;
  count_recent: number;
}
export interface Projection {
  avg_distance_per_day: number;
  avg_distance_per_month: number;
  projected_distance_next_12mo: number;
  projected_odometer_in_12mo: number;
}
export type FactTone = "good" | "watch" | "neutral";
export interface HeadlineFact {
  kind: "economy" | "cost" | "service" | "interval" | "spend" | "mileage" | "milestone";
  text: string;
  tone: FactTone;
}
export interface SpendSummary {
  fuel_lifetime: number;
  service_lifetime: number;
  total_lifetime: number;
  fuel_recent_12mo: number;
  service_recent_12mo: number;
  total_recent_12mo: number;
  total_prior_12mo: number;
  total_delta_12mo: number;
  total_pct_change_12mo: number | null;
  direction: "up" | "down" | "flat" | null;
  avg_per_month: number | null;
}
export interface GarageSignals {
  schema: string;
  generated_for: string;
  vehicle: VehicleMeta;
  overall: Overall;
  economy: Economy;
  cost_per_distance: CostPerDistance;
  service: ServiceStats;
  spend_by_category: CategorySpend[];
  spend: SpendSummary;
  projection: Projection | null;
  headline_facts: HeadlineFact[];
}

export const store = {
  // Vehicles
  listVehicles: () => invoke<Vehicle[]>("list_vehicles"),
  getVehicle: (id: string) => invoke<Vehicle | null>("get_vehicle", { id }),
  createVehicle: (input: NewVehicle) => invoke<Vehicle>("create_vehicle", { input }),
  updateVehicle: (id: string, patch: VehiclePatch) => invoke<void>("update_vehicle", { id, patch }),
  deleteVehicle: (id: string) => invoke<void>("delete_vehicle", { id }),
  // Fill-ups
  listFillups: (vehicleId: string, limit = 50000) => invoke<Fillup[]>("list_fillups", { vehicleId, limit }),
  getFillup: (id: string) => invoke<Fillup | null>("get_fillup", { id }),
  createFillup: (input: NewFillup) => invoke<Fillup>("create_fillup", { input }),
  updateFillup: (id: string, patch: FillupPatch) => invoke<void>("update_fillup", { id, patch }),
  deleteFillup: (id: string) => invoke<void>("delete_fillup", { id }),
  // Services
  listServices: (vehicleId: string, limit = 50000) => invoke<Service[]>("list_services", { vehicleId, limit }),
  getService: (id: string) => invoke<Service | null>("get_service", { id }),
  createService: (input: NewService) => invoke<Service>("create_service", { input }),
  updateService: (id: string, patch: ServicePatch) => invoke<void>("update_service", { id, patch }),
  deleteService: (id: string) => invoke<void>("delete_service", { id }),
  /** Compute the derived-signals object for ONE vehicle on-device. `today` = client local YYYY-MM-DD. */
  computeSignals: (vehicleId: string, today: string) => invoke<GarageSignals>("compute_signals", { vehicleId, today }),
};

// ── Pure policy / display helpers ──────────────────────────────────────────────

/** Curated service categories; user can also type their own. Kept generic + observational —
 *  these are LABELS for the user's own log, not maintenance recommendations. */
export const SERVICE_KINDS = [
  "oil-change", "tires", "brakes", "battery", "inspection", "filters",
  "wipers", "coolant", "transmission", "registration", "wash", "repair",
] as const;

/** Pretty-print a normalized kind: "oil-change" -> "Oil change". */
export function prettyKind(kind: string): string {
  const s = kind.replace(/-/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : kind;
}
/** Normalize a kind — lowercase, trimmed, whitespace→hyphen. */
export function normalizeKind(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "-");
}

export const ECONOMY_UNIT = (v: Pick<Vehicle, "distance_unit" | "volume_unit">): string =>
  v.distance_unit === "km" ? "km/L" : "mi/gal";

/** Format a number with thousands separators + a fixed precision. */
export function fmtNum(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Friendly date from an RFC3339 ts or a YYYY-MM-DD string. */
export function fmtDate(s: string): string {
  if (!s) return "";
  const d = toLocalDate(s) ?? new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** "3 months ago" / "12 days ago" — for the "your last X was…" facts. */
export function relativeAge(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 45) return `${days} days ago`;
  const months = Math.round(days / 30.4375);
  if (months < 18) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = (days / 365).toFixed(1).replace(/\.0$/, "");
  return `${years} years ago`;
}

/** Display string for the vehicle make/model/year line. */
export function vehicleSubtitle(v: Vehicle): string {
  const parts: string[] = [];
  if (v.year > 0) parts.push(String(v.year));
  if (v.make) parts.push(v.make);
  if (v.model) parts.push(v.model);
  return parts.join(" ");
}

/** Economy unit string for a vehicle (e.g. "mi/gal"). */
export function economyUnit(v: Pick<Vehicle, "distance_unit">): string {
  return v.distance_unit === "km" ? "km/L" : "mi/gal";
}

/** A compact signed-percent string ("+18%" / "-9%"), or null when no change to show. */
export function pctLabel(p: number | null | undefined): string | null {
  if (p === null || p === undefined || Number.isNaN(p)) return null;
  const r = Math.round(p);
  if (r === 0) return null;
  return `${r > 0 ? "+" : ""}${r}%`;
}

/** Initials/short token for a vehicle (segmented switcher label fallback). */
export function vehicleShortName(v: Vehicle): string {
  return v.name.length > 16 ? v.name.slice(0, 15) + "…" : v.name;
}
